import * as React from 'react'
import { alpha } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

import DeleteIcon from '@mui/icons-material/Delete'
import FilterListIcon from '@mui/icons-material/FilterList'
import { Divider, Pagination } from '@mui/material'
import sortIcon from 'src/assets/icons/sortIcon.png'
import styles from './index.module.scss'
import CircleCheckbox from '../CircleCheckbox'
import SimpleDropdown from '../SimpleDropdown'
import deleteIcon from 'src/assets/icons/deleteIcon.png'
import ConfirmationBox from '../ConfirmationBox'

export interface HeadCellI {
  id: string
  label: string
  isCheckBox?: boolean
  sortable: boolean
  align?: 'center' | 'left' | 'right'
}

type PaginatedTableProps = {
  title: string
  headCells: HeadCellI[]
  children?: React.ReactElement[] | React.ReactElement
  currentPage: number
  onPageChange: (newPage: number) => void
  totalPages: number
  onSortClick: (sortOrderBy: string) => void
  onSelectAllClick?: (checked: boolean) => void
  isAllSelected?: boolean
  numSelected?: number
  onDeleteSelected?: () => void
  toolbarFilter?: JSX.Element
}

export type SortOrderI = 'asc' | 'desc'
export default function PaginatedTable({
  title,
  headCells,
  children,
  totalPages,
  currentPage,
  isAllSelected,
  numSelected,
  onPageChange,
  onSortClick,
  onSelectAllClick,
  onDeleteSelected,
  toolbarFilter
}: PaginatedTableProps) {
  // Avoid a layout jump when reaching the last page with empty rows.
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    onPageChange(value)
    console.log(value)
  }

  return (
    <Box sx={{ width: '100%', }}>
      <Paper sx={{ width: '100%', mb: 2 }} className={styles.paginatedTable}>
        <EnhancedTableToolbar
          numSelected={numSelected}
          title={title}
          onDeleteSelected={onDeleteSelected}
          toolbarFilter={toolbarFilter}
        />
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby={title}>
            <EnhancedTableHead
              isAllSelected={isAllSelected}
              onSelectAllClick={onSelectAllClick}
              onSortClick={onSortClick}
              headCells={headCells}
            />
            <TableBody>{children}</TableBody>
          </Table>
        </TableContainer>
        <Box mt={5} p={2} display='flex' justifyContent='center'>
          <Pagination
            count={totalPages}
            shape='rounded'
            onChange={handlePageChange}
            page={currentPage}
          />
        </Box>
      </Paper>
    </Box>
  )
}

interface EnhancedTableProps {
  onSortClick: (sortOrderBy: string) => void
  onSelectAllClick?: (checked: boolean) => void
  isAllSelected?: boolean
  headCells: HeadCellI[]
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { isAllSelected = false, headCells, onSortClick, onSelectAllClick } = props

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => {
          if (headCell.isCheckBox && onSelectAllClick) {
            return (
              <TableCell padding='checkbox' key={headCell.label}>
            
                <CircleCheckbox
                  checked={isAllSelected}
                  onChange={(checked) => onSelectAllClick(checked)}
                />
              </TableCell>
            )
          }
          return (
            <TableCell key={headCell.label} align={headCell.align}>
              {headCell.label}

              {headCell.sortable && (
                <IconButton onClick={() => onSortClick(headCell.id)}>
                  {' '}
                  <img src={sortIcon} alt='sortIcon' />{' '}
                </IconButton>
              )}
            </TableCell>
          )
        })}
      </TableRow>
    </TableHead>
  )
}

interface EnhancedTableToolbarProps {
  numSelected?: number
  title: string
  onDeleteSelected?: () => void
  toolbarFilter?: JSX.Element
}

function EnhancedTableToolbar(props: EnhancedTableToolbarProps) {
  const { numSelected = 0, title, onDeleteSelected , toolbarFilter} = props
  const [toggleDeleteConfirmation, setToggleDeleteConfirmation] = React.useState(false)

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography sx={{ flex: '1 1 100%' }} color='inherit' variant='h4'>
          {numSelected} selected
        </Typography>
      ) : (
        <Typography sx={{ flex: '1 1 100%' }} variant='h2' id='tableTitle'>
          {title}
        </Typography>
      )}
      {numSelected > 0 ? (
        <Tooltip title='Delete'>
          <IconButton onClick={() => setToggleDeleteConfirmation(true)}>
            <img src={deleteIcon} alt='deleteIcon' />
          </IconButton>
        </Tooltip>
      ) : (
        <>
        {
          !!toolbarFilter && toolbarFilter
        }
        
      
        </>
      )}
      <ConfirmationBox
        title='Are you sure you want to Delete all selected items.'
        open={toggleDeleteConfirmation}
        onReject={() => setToggleDeleteConfirmation(false)}
        onAccept={() => {
          onDeleteSelected && onDeleteSelected()
          setToggleDeleteConfirmation(false)
        }}
      />
    </Toolbar>
  )
}
